import type { OrderClaim } from './OrderClaims';
export type StatusCorrection = {version:number;source_version:string;processing_status:string|null;refund_status:string|null;reason:string;updated_at:string|null;
  events:{at:string;axis:string;before:string|null;after:string;reason:string}[]};

export const CLAIM_STAGES = [
  ['cancel_requested', '취소 접수'], ['cancel_progress', '취소 진행'], ['cancel_completed', '취소 완료'],
  ['return_requested', '반품 접수'], ['return_progress', '반품 진행'], ['return_completed', '반품 완료'],
  ['refund_requested', '환불 접수'], ['refund_progress', '환불 진행'], ['refund_completed', '환불 완료'],
] as const;

export function claimStage(claim: Pick<OrderClaim, 'kind' | 'status' | 'refund_status'>) {
  if (claim.kind === 'return' && claim.status === 'received') return 'return_completed';
  if (claim.status === 'unavailable' || claim.refund_status === 'unavailable') return `${claim.kind}_unavailable`;
  if (claim.refund_status === 'completed') return `${claim.kind}_completed`;
  return `${claim.kind}_${claim.status === 'requested' && !['processing', 'review'].includes(claim.refund_status) ? 'requested' : 'progress'}`;
}

export function claimStageLabel(claim: Pick<OrderClaim, 'kind' | 'status' | 'refund_status'>) {
  return CLAIM_STAGES.find(([key]) => key === claimStage(claim))?.[1]
    || `${({ cancel: '취소', return: '반품', refund: '환불' })[claim.kind]} 불가`;
}

export const CLAIM_PROCESSING_STATES = [['requested','접수'],['approved','승인'],['collecting','회수 중'],['received','회수 완료'],['completed','처리 완료'],['unavailable','처리 불가']] as const;
export const CLAIM_REFUND_STATES = [['none','환불 없음'],['pending','환불 대기'],['processing','환불 중'],['review','환불 확인 필요'],['completed','환불 완료'],['unavailable','환불 불가']] as const;
export const CLAIM_WORKFLOW_FILTERS = [
  ['requested','접수'], ['approved','승인 · 환불 대기'], ['return_collecting','반품 처리 중'],
  ['return_received','회수 완료 · 환불 대기'], ['refund_processing','환불 처리 중'], ['refund_review','환불 확인 필요'],
  ['order_cancelled','주문 취소'], ['payment_cancelled','결제 취소'], ['return_completed','반품 완료'],
  ['return_rejected','반품 반려'], ['refund_unavailable','환불 불가'],
] as const;
export function claimWorkflowState(record: {kind?:string;status?:string;refund_status:string;transfer_status?:string|null}) {
  if (record.status==='unavailable' || record.refund_status==='unavailable') return record.kind==='return'?'return_rejected':'refund_unavailable';
  if (record.transfer_status==='completed' || record.refund_status==='completed') return record.kind==='return'?'return_completed':'payment_cancelled';
  if (record.refund_status==='review') return 'refund_review';
  if (record.transfer_status==='pending' || record.refund_status==='processing') return 'refund_processing';
  if (record.status==='requested') return 'requested';
  if (record.kind==='return' && record.status==='collecting') return 'return_collecting';
  if (record.kind==='return' && record.status==='received') return 'return_received';
  if (record.status==='approved' || record.refund_status==='pending') return 'approved';
  return 'order_cancelled';
}
// The seller workflow follows actual processing records, never display-only corrections.
export function claimWorkflowLabel(record: { kind?: string; status?: string; refund_status: string; transfer_status?: string | null;
  is_automatic?: boolean; restored?: boolean }, paymentMethod?: string | null, receiptStatus?: string | null) {
  if (record.is_automatic) return '기한 취소';
  if (record.restored) return '입금 확인';
  if (record.status === 'unavailable' || record.refund_status === 'unavailable') return record.kind === 'return' ? '반품 반려' : '환불 불가';
  const refunded = record.transfer_status === 'completed' || record.refund_status === 'completed';
  if (refunded) {
    if (paymentMethod === 'bank' && receiptStatus === 'pending') return '현금영수증 취소 대기';
    return record.kind === 'return' ? '반품 완료' : '결제 취소';
  }
  if (record.transfer_status === 'pending') return '환불 대기';
  if (record.refund_status === 'review') return '환불 확인 필요';
  if (record.refund_status === 'processing') return paymentMethod === 'bank' ? '환불 대기' : 'PG 환불 처리 중';
  if (record.status === 'requested') return '접수';
  if (record.kind === 'return' && record.status === 'collecting') return '반품 처리 중';
  if (record.kind === 'return' && record.status === 'received') return paymentMethod === 'bank' ? '환불 대기' : 'PG 환불 대기';
  if (record.refund_status === 'pending') return paymentMethod === 'bank' ? '환불 대기' : 'PG 환불 대기';
  return '주문 취소';
}
export function bankClaimNeedsAttention(claim: Pick<OrderClaim, 'kind' | 'status' | 'refund_status'> & { transfer_status?: string | null }, paid: boolean, paymentMethod?: string | null) {
  if (paymentMethod !== 'bank') return false;
  if (claim.transfer_status === 'pending') return true;
  return paid && claim.transfer_status !== 'completed'
    && ['requested', 'approved', 'collecting', 'received'].includes(claim.status)
    && (['pending', 'processing', 'review'].includes(claim.refund_status)
      || claim.kind !== 'refund' && claim.refund_status === 'none');
}
export function claimProcessingState(claim: Pick<OrderClaim,'kind'|'status'|'refund_status'|'status_correction'>) {
  if(claim.status_correction?.processing_status)return claim.status_correction.processing_status;
  if (claim.status==='unavailable') return 'unavailable';
  if (claim.kind!=='return' && claim.refund_status==='completed') return 'completed';
  return claim.status;
}
export function claimProcessingLabel(claim: Pick<OrderClaim,'kind'|'status'|'refund_status'|'status_correction'>) {
  const state=claimProcessingState(claim), kind={cancel:'취소',return:'반품',refund:'환불'}[claim.kind];
  if(state==='collecting') return '회수 중';
  if(state==='received') return '회수 완료';
  return `${kind} ${({requested:'접수',approved:'승인',completed:'처리 완료',unavailable:'불가'} as Record<string,string>)[state]||'상태 확인 필요'}`;
}
export function claimRefundState(claim: {refund_status:string;transfer_status?:string|null;status_correction?:StatusCorrection}) {
  if(claim.status_correction?.refund_status)return claim.status_correction.refund_status;
  if(claim.transfer_status==='pending') return 'processing';
  if(claim.transfer_status==='completed') return 'completed';
  return claim.refund_status;
}
export function claimRefundLabel(claim: {kind?:string;refund_status:string;transfer_status?:string|null;status_correction?:StatusCorrection}) {
  if(claim.status_correction?.refund_status==='none')return '환불 없음';
  if(claim.status_correction?.refund_status)return CLAIM_REFUND_STATES.find(([value])=>value===claim.status_correction!.refund_status)?.[1]||'환불 상태 확인 필요';
  if(claim.transfer_status==='pending') return '환불 중 · 송금 대기';
  if(claim.transfer_status==='completed') return '환불 완료 · 송금 완료';
  if(claim.refund_status==='none') return '환불 없음';
  return CLAIM_REFUND_STATES.find(([value])=>value===claimRefundState(claim))?.[1]||'환불 상태 확인 필요';
}

export type OrderItemCancellation = { seq: number; detail_seq?: number; quantity: number; refund_status: string;
  payment_method?: string | null; transfer_status?: string | null; status_correction?: StatusCorrection; restored?: boolean };
export function orderItemClaimStates(detailSeq: number, claims: OrderClaim[] = [], cancellations: OrderItemCancellation[] = [], purchasedQuantity = 0) {
  const states = claims.flatMap(claim => {
    const item = claim.items.find(item => item.detail_seq === detailSeq);
    if (!item) return [];
    const current = { ...claim, status_correction: item.status_correction || claim.status_correction,
      refund_status: item.refund_status ?? claim.refund_status,
      transfer_status: item.transfer_status === undefined ? claim.transfer_status : item.transfer_status };
    if (claimProcessingState(current) === 'unavailable'
      || claimRefundState(current) === 'unavailable' && claimProcessingState(current) !== 'received') return [];
    return [{ seq: claim.seq, processing: `${item.quantity < item.purchased_quantity ? '부분 ' : ''}${claimProcessingLabel(current)}`,
      refund: claimRefundState(current) === 'none' ? '' : claimRefundLabel(current) }];
  });
  const linked = new Set(claims.flatMap(claim => claim.cancellation_seqs || []));
  for (const record of cancellations.filter(record => record.detail_seq === detailSeq && !record.restored
    && record.refund_status !== 'none' && !linked.has(record.seq))) {
    const current = { ...record, transfer_status: record.transfer_status ?? (record.payment_method === 'bank'
      && ['pending', 'completed'].includes(record.refund_status) ? record.refund_status : null) };
    if (claimRefundState(current) === 'unavailable' || record.status_correction?.processing_status === 'unavailable') continue;
    const partial = purchasedQuantity > record.quantity ? '부분 ' : '';
    states.push({ seq: -record.seq, processing: partial + (record.status_correction?.processing_status
      ? claimProcessingLabel({ kind: 'refund', status: 'approved', ...current })
      : claimRefundState(current) === 'completed' ? '환불 처리 완료' : '환불 처리'), refund: claimRefundLabel(current) });
  }
  return states;
}

export function clampClaimQuantity(value: string, maximum: number, minimum = 0) {
  if (value === '') return '';
  const number = Number(value), limit = Math.max(0, Math.trunc(maximum) || 0), floor = Math.min(limit, Math.max(0, minimum));
  return String(Math.min(limit, Math.max(floor, Number.isFinite(number) ? Math.trunc(number) : floor)));
}

export function claimTime(value?: string | null) {
  if (!value) return '-';
  // API claim dates are UTC; older detail dates already carry the Korean display format.
  const date = new Date(/Z$|[+-]\d\d:\d\d$/.test(value) ? value : `${value.replace(' ', 'T')}Z`);
  if (Number.isNaN(date.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date);
  return parts;
}
